import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { apiClient } from '../../lib/api-client';
import { ReconciliationPreviewDialog } from './ReconciliationPreviewDialog';
import type { ProposedStep } from './ReconciliationPreviewDialog';
import {
  EntityType,
  PromptEntry,
  TaxonomyToolsEditor,
  TaxonomyToolsValue,
  ToolRef,
  WorkflowTypeConfig,
} from './TaxonomyToolsEditor';

interface EntityEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  streamId: string;
  entityType: EntityType;
  entityId: string;
  entityConfig: any;
}


export function EntityEditDialog({
  open,
  onClose,
  onSaved,
  streamId,
  entityType,
  entityId,
  entityConfig,
}: EntityEditDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  const [taxToolsValue, setTaxToolsValue] = useState<TaxonomyToolsValue>({
    tools: [],
    prompts: {},
    workflowTypes: [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconcileSteps, setReconcileSteps] = useState<ProposedStep[]>([]);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcilePayload, setReconcilePayload] = useState<any>(null);

  const initFromConfig = useCallback(() => {
    setDisplayName(entityConfig?.displayName || '');
    setDescription(entityConfig?.agentDescription || entityConfig?.description || '');

    const existingTools: ToolRef[] = entityConfig?.tools || [];

    const existingPrompts: Record<string, PromptEntry> = {};
    if (entityConfig?.prompts) {
      for (const [k, v] of Object.entries(entityConfig.prompts)) {
        if (v && typeof v === 'object' && 'text' in (v as any)) {
          existingPrompts[k] = v as PromptEntry;
        } else if (typeof v === 'string') {
          existingPrompts[k] = { text: v, mode: 'overlay' };
        }
      }
    }
    const taxonomy = entityConfig?.taxonomy;
    const wts = (taxonomy?.workflowTypes || []) as WorkflowTypeConfig[];
    setTaxToolsValue({
      tools: existingTools,
      prompts: existingPrompts,
      workflowTypes: wts,
    });
  }, [entityConfig]);

  useEffect(() => {
    if (open) {
      initFromConfig();
      setError(null);
    }
  }, [open, initFromConfig]);

  const performDirectSave = async (savePayload: any) => {
    const id = entityId;
    switch (entityType) {
      case 'stream':
        await apiClient.updateAiConfigStream(id, JSON.stringify(savePayload));
        break;
      case 'product':
        await apiClient.updateAiConfigProduct(streamId, id, savePayload);
        break;
      case 'team':
        await apiClient.updateAiConfigTeam(streamId, id, savePayload);
        break;
      case 'tenant':
        await apiClient.updateAiConfigTenant(streamId, id, savePayload);
        break;
      case 'role':
        await apiClient.updateAiConfigRole(streamId, id, savePayload);
        break;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        displayName: displayName || undefined,
        taxonomy: taxToolsValue.workflowTypes.length > 0 ? { workflowTypes: taxToolsValue.workflowTypes } : undefined,
        tools: taxToolsValue.tools.length > 0 ? taxToolsValue.tools : undefined,
        prompts: Object.keys(taxToolsValue.prompts).length > 0 ? taxToolsValue.prompts : undefined,
      };

      if (entityType === 'role') {
        payload.agentDescription = description || undefined;
      }

      if (entityType === 'role' || entityType === 'stream') {
        const previewBody =
          entityType === 'role'
            ? { streamId, entityType: 'role' as const, entityId, patch: payload }
            : {
                streamId,
                entityType: 'stream' as const,
                entityId: streamId,
                yaml: JSON.stringify(payload),
              };
        const preview = await apiClient.previewReconciliation(previewBody);
        const proposedSteps = preview.proposedSteps ?? [];
        if (proposedSteps.length > 0) {
          setReconcileSteps(proposedSteps as ProposedStep[]);
          setReconcilePayload(previewBody);
          setReconcileOpen(true);
          return;
        }
      }

      await performDirectSave(payload);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>
            Edit {entityType.charAt(0).toUpperCase() + entityType.slice(1)}: {entityId}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <Tabs defaultValue="general">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="taxonomy-tools">Taxonomy &amp; Tools</TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
                  <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                </div>
                {(entityType === 'role' || entityType === 'stream') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Stream:</span> {streamId} &middot;{' '}
                  <span className="font-medium">Type:</span> {entityType} &middot;{' '}
                  <span className="font-medium">ID:</span> {entityId}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="taxonomy-tools">
              <TaxonomyToolsEditor
                open={open}
                streamId={streamId}
                entityType={entityType}
                entityId={entityId}
                value={taxToolsValue}
                onChange={setTaxToolsValue}
                draftConfigExtras={
                  entityType === 'role'
                    ? {
                        products: Array.isArray(entityConfig?.products) ? entityConfig.products : [],
                        teams: Array.isArray(entityConfig?.teams) ? entityConfig.teams : [],
                        tenants: Array.isArray(entityConfig?.tenants) ? entityConfig.tenants : [],
                      }
                    : undefined
                }
              />
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {reconcilePayload != null && (
    <ReconciliationPreviewDialog
      open={reconcileOpen}
      onClose={() => { setReconcileOpen(false); }}
      proposedSteps={reconcileSteps}
      mode="edit"
      executePayload={reconcilePayload}
      onExecuted={(_results) => {
        setReconcileOpen(false);
        onSaved();
      }}
      onConfigOnlySave={async () => {
        const p = reconcilePayload;
        if (!p) return;
        try {
          const savePayload =
            p.entityType === 'stream' && p.yaml != null
              ? JSON.parse(p.yaml)
              : p.patch;
          await performDirectSave(savePayload);
          setReconcileOpen(false);
          onSaved();
        } catch (err: any) {
          setError(err.message || 'Save failed');
        }
      }}
    />
    )}
    </>
  );
}
